import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';

import { ILootboxOwned } from 'models/lootbox/i-lootbox-owned.interface';
import { IState } from 'models/reducers/i-state.interface';

import CircleSpinner from 'components/circleSpinner/CircleSpinner';

import { useWindowSize } from 'hooks/useWindowSize';

import LootboxCardLarge from '../lootboxCard/lootboxCardLarge/LootboxCardLarge';
import LootboxOwnedEmptySection from '../lootboxOwnedEmptySection/LootboxOwnedEmptySection';
import LootboxOwnedItem from '../lootboxOwnedItem/LootboxOwnedItem';
import LootboxSection from '../lootboxSection/LootboxSection';
import styles from './LootboxOwned.module.scss';

interface ISliderConfig {
  maxWidth?: number;
  desktopSlidesToShow?: number;
  mobileSlidesToShow?: number;
}

interface ILootboxOwnedProps {
  className?: string;
  lootboxes?: ILootboxOwned[];
  loading: boolean;
  fetchLootboxesDetails: () => Promise<void>;
  openBuyLootboxes: () => void;
  sliderConfig?: ISliderConfig;
}

const LootboxOwned = ({
  className,
  loading,
  lootboxes,
  fetchLootboxesDetails,
  openBuyLootboxes,
  sliderConfig,
}: ILootboxOwnedProps) => {
  const wrapperElement = useRef<HTMLDivElement>(null);
  const [width] = useWindowSize();
  const [visibleSlides, setVisibleSlides] = useState(3);
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const updateToken = useSelector((state: IState) => state.lootbox.updateToken);
  const defaultCardWidth = 384;

  useEffect(() => {
    if (!walletAddress) {
      return;
    }

    fetchLootboxesDetails();
  }, [walletAddress, updateToken, fetchLootboxesDetails]);

  useEffect(() => {
    if (sliderConfig) {
      // Use custom slider configuration
      const isMobile = width < 768; // Standard mobile breakpoint
      if (isMobile) {
        setVisibleSlides(sliderConfig.mobileSlidesToShow || 1);
      } else {
        setVisibleSlides(sliderConfig.desktopSlidesToShow || 3);
      }
      return;
    }

    // Default behavior
    const wrapperVideo = wrapperElement.current?.clientWidth || width;

    if (wrapperVideo <= defaultCardWidth * 2) {
      setVisibleSlides(1);
      return;
    }

    if (wrapperVideo < defaultCardWidth * 3) {
      setVisibleSlides(2);
      return;
    }

    setVisibleSlides(Math.floor(wrapperVideo / defaultCardWidth));
  }, [width, sliderConfig]);

  if (!walletAddress) {
    return <></>;
  }

  if (!loading && !lootboxes?.length) {
    return <LootboxOwnedEmptySection className={className} openBuyLootboxes={openBuyLootboxes} />;
  }

  return (
    <>
      <LootboxSection className={className}>
        {loading ? (
          <div className={styles['lootbox-owned__loading-section']}>
            <CircleSpinner size={48} />
          </div>
        ) : (
          <div
            ref={wrapperElement}
            style={sliderConfig?.maxWidth ? { maxWidth: `${sliderConfig.maxWidth}px`, margin: '0 auto' } : {}}
          >
            <Slider
              dots
              variableWidth
              speed={300}
              rows={1}
              arrows={false}
              infinite={false}
              dotsClass={`slick-dots ${styles['lootbox-owned__carousel-dots']}`}
              className={styles['lootbox-owned__slider']}
              slidesToScroll={1}
              slidesToShow={visibleSlides}
            >
              {lootboxes?.map(({ id, address, quantityOwned, metadata }) => (
                <div className={styles['lootbox-owned__card-wrapper']} key={`lootbox-${id}`}>
                  <LootboxCardLarge
                    autoplay
                    loop
                    title={metadata?.name}
                    background={metadata?.image}
                    className={styles['lootbox-owned__card']}
                  >
                    <div className={styles['lootbox-owned__card-content']}>
                      <LootboxOwnedItem
                        assetId={id}
                        address={address}
                        quantityOwned={+quantityOwned}
                        metadata={metadata}
                      />
                    </div>
                  </LootboxCardLarge>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </LootboxSection>
    </>
  );
};

export default LootboxOwned;
